import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactJson from 'react-json-view';

import {
  Box,
  Grid,
  IconButton,
  LinearProgress,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';

import Canvas from '../../redux/containers/visualizer/Canvas';
import { PREDICTION_TYPE_DISPLAY_NAME, formatCurrency, READING_AI_TYPES, SHELF_READING_AI_TYPES } from '../tools/Canvas/utils';

const formatPercent = value => {
  const num = parseFloat(value);
  if (!Number.isFinite(num)) return '-';
  return (num * 100).toFixed(2) + '%';
};

const formatValue = value => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const DetectionModal = props => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(props.open);
  const theme = useTheme();
  const [labelMetadata, setlabelMetadata] = useState(null);
  const [id, setId] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const loadingConditionImage = props.images && props.images.image && !props.isLoadingImages && props.images.image.length > 0;
  const selectedImage = props.images?.image?.[0];
  const selectedImageBlobPath = selectedImage?.image_url ?? null;
  const readingCaptureId = props.selectedLabel?.split('_')[0];
  const shelfoutDetection = props.images?.metadata?.[props.aisle]?.find(
    detection => detection.detection_id === props.selectedLabel,
  );

  // Sign the secondary image ONLY when its blob_path actually changes.
  // Previously this lived inside a useEffect that depended on derived arrays
  // (readingMetadata, selectedImage), which produced new references every render
  // and caused an infinite request loop on /api/visualize/sign_image.
  useEffect(() => {
    if (selectedImageBlobPath) {
      props.signSecondaryImage({ store: props.store, blob_path: selectedImageBlobPath });
    }
  }, [selectedImageBlobPath, props.store]);

  useEffect(() => {
    setIsOpen(props.open);
    setlabelMetadata(null);
    setId(null);
    setConfidence(null);
    if (selectedImageBlobPath && props.ai !== 'Shelfout Detection') {
      if (props.ai === 'Label Detection' || props.ai === 'Product Detection' || props.ai === 'Overhead Inventory Box Detection') {
        setlabelMetadata(props.images?.metadata?.[props.selectedLabel] ?? null);
      } else {
        const isReading = READING_AI_TYPES.includes(props.ai);
        if (isReading && Array.isArray(props.preMetadata) && props.selectedLabel) {
          setlabelMetadata(props.preMetadata.filter(item => item.detection_id === props.selectedLabel));
        } else {
          setlabelMetadata(props.preMetadata ?? null);
        }
      }
    }
    if (selectedImageBlobPath) {
      if (READING_AI_TYPES.includes(props.ai)) {
        const readingMeta = readingCaptureId ? props.images?.metadata?.[readingCaptureId] ?? [] : [];
        setId(readingCaptureId ?? null);
        setConfidence(readingMeta.find(e => e.detection_id === props.selectedLabel)?.score ?? null);
      } else if (props.ai === 'Product Detection') {
        setId(props.selectedLabel);
        setConfidence(
          props.preMetadata?.find(e => e.product_detection_id === props.selectedLabel)
            ?.capture_detection_score ?? null,
        );
      } else {
        setId(props.selectedLabel);
        setConfidence(props.preMetadata?.find(e => e.detection_id === props.selectedLabel)?.score ?? null);
      }
    }
  }, [props.open, props.images, props.selectedLabel, props.ai, props.preMetadata, selectedImageBlobPath, readingCaptureId]);

  const isLight = theme.palette.type === 'light';
  const textColor = isLight ? theme.palette.text.primary : 'white';

  // ── Styles ────────────────────────────────────────────────────────────────

  const styles = {
    // Modal
    modal: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '80%',
      maxWidth: '1100px',
      bgcolor: theme.palette.paper.main,
      maxHeight: '90vh',
      p: 3,
      overflow: 'hidden',
      overflowY: !loadingConditionImage ? 'hidden' : 'auto',
      borderRadius: '5px',
      outline: 'none',
      boxShadow: 24,
      border: `1px solid ${theme.palette.divider}`,
    },

    // Header
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      pb: 1,
      mb: 1.5,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerTitle: {
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      fontSize: '0.8rem',
    },
    closeButton: {
      color: textColor,
      fontSize: '1.25rem',
      lineHeight: 1,
      fontWeight: 300,
      p: 0.25,
    },

    // Canvas
    canvasWrapper: { display: 'flex', flexDirection: 'column' },
    canvasBox: { display: 'flex', justifyContent: 'center', alignItems: 'flex-start', width: '100%' },
    canvas: {
      width: '26em',
      maxHeight: '38em',
      overflow: 'auto',
      border: 'none',
      '& canvas': { display: 'block', marginBottom: '0 !important' },
    },

    // Table
    tableContainer: {
      borderRadius: 2,
      overflow: 'hidden',
      bgcolor: 'transparent',
      border: `1px solid ${isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)'}`,
      boxShadow: 'none',
    },
    table: {
      width: '100%',
      '& .MuiTableCell-root': {
        borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)'}`,
        py: 2,
        px: 3,
        wordBreak: 'break-word',
      },
      '& thead .MuiTableCell-root': {
        py: 1.75,
        bgcolor: isLight ? 'rgba(0,0,0,0.015)' : 'rgba(255,255,255,0.02)',
        borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)'}`,
      },
      '& tbody tr': { transition: 'background-color 120ms ease' },
      '& tbody tr:hover': { bgcolor: isLight ? 'rgba(0,0,0,0.015)' : 'rgba(255,255,255,0.025)' },
      '& tbody tr:last-of-type .MuiTableCell-root': { borderBottom: 'none' },
      '& tbody td:first-of-type p': { opacity: 0.55, fontSize: '0.82rem', fontWeight: 500 },
      '& tbody td:nth-of-type(2) p': { fontWeight: 600, fontSize: '0.875rem' },
      '& tbody td:last-of-type p': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8rem' },
    },
    tableHeaderCell: {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tableHeaderText: {
      fontWeight: 700,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      opacity: 0.5,
      fontSize: '0.65rem',
    },
    idMonospace: {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    },

    // Metadata
    metadataBox: { mt: 3 },
    metadataLabel: {
      fontWeight: 700,
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      mb: 1.5,
      opacity: 0.5,
      fontSize: '0.65rem',
    },
    metadataPre: {
      m: 0,
      p: 2.5,
      borderRadius: 1.5,
      bgcolor: '#18181b',
      border: `1px solid ${isLight ? '#18181b' : 'rgba(255,255,255,0.08)'}`,
      maxHeight: '14rem',
      overflow: 'auto',
      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)',
    },

    // Progress
    linearProgress: {
      width: '99.5%',
      backgroundColor: theme.palette.background.default,
    },
  };

  const headerTitle = props.ai === 'Shelfout Detection'
    ? t('hip_app.general.product_detection') || 'Product detail'
    : SHELF_READING_AI_TYPES.includes(props.ai)
      ? t('hip_app.general.label_reading') || 'Label detail'
      : t('hip_app.general.item_info') || 'Detection detail';

  return (
    <Modal open={isOpen} onClose={props.onClose}>
      <Box sx={styles.modal}>
        {loadingConditionImage && (
          <Box sx={styles.header}>
            <Typography
              variant="h6"
              component="h2"
              fontFamily={theme.typography.fontFamily}
              sx={styles.headerTitle}
              color={textColor}>
              {headerTitle.toUpperCase()}
            </Typography>
            <IconButton onClick={props.onClose} size="small" aria-label="close" sx={styles.closeButton}>
              ×
            </IconButton>
          </Box>
        )}
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
          {props.ai !== 'Shelfout Detection' && (
            <Grid item xs={12} md={5} sx={styles.canvasWrapper}>
              <Box sx={styles.canvasBox}>
                {!props.isLoadingImages ? (
                  loadingConditionImage ? (
                    <Canvas
                      metadata={props.images?.metadata?.[id] ?? []}
                      onModal={true}
                      sx={styles.canvas}
                      currencyFormat={props.currencyFormat}
                      formatCurrency={props.formatCurrency}
                      image_url={props.image_url}
                      selectedLabel={
                        READING_AI_TYPES.includes(props.ai)
                          ? props.selectedLabel
                          : null
                      }
                    />
                  ) : (
                    <Typography
                      variant="h6"
                      component="p"
                      textAlign="center"
                      marginTop="-1rem"
                      fontFamily={theme.typography.fontFamily}
                      color={textColor}>
                      {'No image found'}
                    </Typography>
                  )
                ) : null}
              </Box>
            </Grid>
          )}
          <Grid item xs={12} md={props.ai !== 'Shelfout Detection' ? 7 : 12}>
            {loadingConditionImage ? (
              <Box>
                <TableContainer sx={styles.tableContainer}>
                  <Table sx={styles.table} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={styles.tableHeaderCell}>
                          <Typography variant="caption" component="p" fontFamily={theme.typography.fontFamily} sx={styles.tableHeaderText} color={textColor}>
                            {t('hip_app.general.type')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={styles.tableHeaderCell}>
                          <Typography variant="caption" component="p" fontFamily={theme.typography.fontFamily} sx={styles.tableHeaderText} color={textColor}>
                            {t('hip_app.general.value')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={styles.tableHeaderCell}>
                          <Typography variant="caption" component="p" fontFamily={theme.typography.fontFamily} sx={styles.tableHeaderText} color={textColor}>
                            {t('hip_app.general.confidence')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingConditionImage && labelMetadata
                        ? labelMetadata.filter(item => !item.reading_stage).map((item, index) => {
                          const typeKey = item.prediction_type ?? item.class;
                          const toTitleCase = str => String(str).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                          const typeName = PREDICTION_TYPE_DISPLAY_NAME[typeKey] ?? (typeKey != null ? toTitleCase(typeKey) : t('hip_app.general.item'));
                          const isPriceType = typeKey === 0 || typeKey === 'regular_price';
                          const rawValue = item.value ?? item.item;
                          const cf = props.currencyFormat || {};
                          const displayValue = isPriceType && props.formatCurrency && rawValue != null
                            ? formatCurrency(String(rawValue), '', cf.currency_thousands_sep, cf.currency_decimal_sep, cf.currency_decimals)
                            : formatValue(rawValue);
                          return (
                            <TableRow key={`${item.detection_id ?? index}-${typeKey}`}>
                              <TableCell scope="row">
                                <Typography variant="body2" component="p" fontFamily={theme.typography.fontFamily} color={textColor}>
                                  {typeof typeKey === 'number' ? `${typeName} (${typeKey})` : typeName}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" component="p" fontFamily={theme.typography.fontFamily} color={textColor}>
                                  {displayValue}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" component="p" fontFamily={theme.typography.fontFamily} color={textColor}>
                                  {Number.isFinite(parseFloat(item.confidence))
                                    ? formatPercent(item.confidence)
                                    : formatPercent(item?.score)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                        : null}
                      <TableRow>
                        <TableCell scope="row">
                          <Typography variant="body2" component="p" fontFamily={theme.typography.fontFamily} color={textColor}>
                            {props.ai === 'Shelfout Detection' ? 'Product Detection ID' : 'Detection ID'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" component="p" sx={styles.idMonospace} color={isLight ? textColor : 'rgba(255,255,255,0.85)'}>
                            {props.selectedLabel}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const pct = props.ai === 'Shelfout Detection'
                              ? formatPercent(shelfoutDetection?.prediction_score)
                              : formatPercent(confidence);
                            const hasValue = pct !== '-';
                            const successColor = isLight ? '#059669' : '#4ade80';
                            const mutedColor = isLight ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)';
                            return (
                              <Typography
                                variant="body2"
                                component="p"
                                sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: hasValue ? 700 : 400, fontSize: '0.85rem' }}
                                color={hasValue ? successColor : mutedColor}>
                                {pct}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                      {props.ai === 'Shelfout Detection' && loadingConditionImage && (
                        <TableRow>
                          <TableCell scope="row">
                            <Typography variant="body2" component="p" fontFamily={theme.typography.fontFamily} color={textColor}>
                              {t('hip_app.general.fixture')}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" component="p" fontFamily={theme.typography.fontFamily} color={textColor}>
                              {props.ai === 'Shelfout Detection' && Number.isFinite(parseFloat(shelfoutDetection?.fixture))
                                ? parseFloat(shelfoutDetection.fixture)
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" component="p" fontFamily={theme.typography.fontFamily} color={textColor}>
                              -
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : null}
            {loadingConditionImage && (labelMetadata || shelfoutDetection) ? (
              <Box sx={styles.metadataBox}>
                <Typography variant="caption" component="p" fontFamily={theme.typography.fontFamily} sx={styles.metadataLabel} color={textColor}>
                  {t('hip_app.general.item_metadata') || 'Metadata'}
                </Typography>
                <Box sx={styles.metadataPre}>
                  <ReactJson
                    src={(() => {
                      const raw = props.ai === 'Shelfout Detection' ? shelfoutDetection : labelMetadata;
                      if (Array.isArray(raw)) return Object.fromEntries(raw.map(item => [item.prediction_type_name ?? item.prediction_type ?? item.class ?? item.detection_id, item]));
                      return raw ?? {};
                    })()}
                    theme="monokai"
                    collapsed={1}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={true}
                    name={false}
                    quotesOnKeys={false}
                    iconStyle="circle"
                    style={{ backgroundColor: 'transparent', fontSize: '0.78rem', lineHeight: 1.7 }}
                  />
                </Box>
              </Box>
            ) : null}
          </Grid>
          {(props.isLoadingImages || props.isLoadingImageUrl) && (
            <LinearProgress sx={styles.linearProgress} />
          )}
        </Grid>
      </Box>
    </Modal>
  );
};

export default DetectionModal;
